import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { Building2, Plus, Check, ChevronDown, X, Trash2 } from 'lucide-react';

const BusinessSwitcher = () => {
  const { currentBusiness, switchBusiness } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [open, setOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ businessName: '', businessType: 'retail', location: '', phone: '' });

  useEffect(() => { fetchBusinesses(); }, []);

  const fetchBusinesses = async () => {
    try {
      const res = await api.get('/businesses');
      setBusinesses(Array.isArray(res.data) ? res.data : []);
    } catch (err) {}
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/businesses', form);
      setBusinesses([...businesses, res.data]);
      switchBusiness(res.data);
      setShowForm(false);
      setForm({ businessName: '', businessType: 'retail', location: '', phone: '' });
    } catch (err) { alert(err.response?.data?.message || 'Failed'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await api.delete(`/businesses/${id}`);
      fetchBusinesses();
      const remaining = businesses.filter(b => b.id !== id);
      if (remaining.length > 0) switchBusiness(remaining[0]);
    } catch (err) { alert(err.response?.data?.message || 'Failed'); }
  };

  return (
    <>
      <button onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-white text-sm hover:bg-white/10">
        <Building2 size={16} className="text-emerald-400" />
        <span className="truncate max-w-[100px]">{currentBusiness?.businessName || 'My Business'}</span>
        <ChevronDown size={14} className="text-gray-400" />
      </button>

      {open && (
        <div className="fixed inset-0 z-50" onClick={() => setOpen(false)}>
          <div className="absolute top-14 right-4 w-72 bg-[#111a14] border border-white/10 rounded-xl shadow-2xl p-2" onClick={e => e.stopPropagation()}>
            {businesses.length === 0 ? (
              <p className="text-gray-500 text-xs text-center py-4">No businesses yet</p>
            ) : (
              businesses.map(b => (
                <div key={b.id} className="flex items-center gap-2 group">
                  <button onClick={() => { switchBusiness(b); setOpen(false); }}
                    className="flex items-center justify-between flex-1 p-2.5 rounded-lg hover:bg-white/5 text-sm">
                    <span className="text-white truncate">{b.businessName}</span>
                    {currentBusiness?.id === b.id && <Check size={14} className="text-emerald-400" />}
                  </button>
                  {businesses.length > 1 && currentBusiness?.id !== b.id && (
                    <button onClick={() => handleDelete(b.id, b.businessName)}
                      className="p-1.5 text-gray-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))
            )}
            <hr className="border-white/5 my-2" />
            <button onClick={() => { setShowForm(true); setOpen(false); }}
              className="flex items-center gap-2 w-full p-2.5 rounded-lg text-emerald-400 hover:bg-emerald-500/10 text-sm">
              <Plus size={14} /> Add Business
            </button>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-[9999] flex items-start justify-center pt-32" style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}>
          <div className="bg-[#111a14] border border-white/10 rounded-2xl p-5 w-[360px] max-w-[95vw]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold text-base">New Business</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-white"><X size={18} /></button>
            </div>
            <form onSubmit={handleCreate} className="space-y-3">
              <input value={form.businessName} onChange={e => setForm({...form, businessName: e.target.value})}
                placeholder="Business Name *" autoFocus
                className="w-full p-2.5 bg-[#0d1611] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500/50" required />
              <select value={form.businessType} onChange={e => setForm({...form, businessType: e.target.value})}
                className="w-full p-2.5 bg-[#0d1611] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500/50">
                <option value="retail">Retail</option><option value="wholesale">Wholesale</option>
                <option value="both">Both</option><option value="service">Service</option>
              </select>
              <input value={form.location} onChange={e => setForm({...form, location: e.target.value})}
                placeholder="Location" className="w-full p-2.5 bg-[#0d1611] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500/50" />
              <input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                placeholder="Phone" className="w-full p-2.5 bg-[#0d1611] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500/50" />
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-2.5 border border-white/10 rounded-lg text-gray-400 text-sm">Cancel</button>
                <button type="submit" className="flex-1 py-2.5 bg-emerald-500 text-white rounded-lg text-sm font-medium">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default BusinessSwitcher;
